import React from 'react';
import styled from 'styled-components';
import { 
  Globe, 
  Handshake, 
  Users,
  Award,
  Target,
  Lightbulb,
  ChevronRight
} from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const AOMASection = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 3rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;
  text-align: center;

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, var(--accent-gold), #ffd940);
    color: var(--primary-blue-dark);
    padding: 0.75rem 1.5rem;
    border-radius: var(--radius-full);
    font-weight: 700;
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-family: var(--font-secondary);
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: var(--text-medium);
    line-height: 1.8;
    max-width: 800px;
    margin: 0 auto 1.5rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContentCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, var(--primary-blue), #004d8f);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.35rem;
    color: var(--text-dark);
  }
`;

const CardText = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
`;

const BenefitsSection = styled.div`
  margin-top: 2rem;
`;

const BenefitsTitle = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--text-dark);
  text-align: center;
  margin-bottom: 2rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  text-align: center;
  transition: var(--transition);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-gold);
  }

  .icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, var(--accent-gold), #ffd940);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: var(--primary-blue-dark);
  }

  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: var(--text-medium);
    line-height: 1.6;
  }
`;

const ProjectsSection = styled.div`
  margin-top: 3rem;
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
`;

const ProjectsTitle = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--accent-gold);
  }
`;

const ProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProjectItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--background-light);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--accent-gold);

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    h4 {
      font-weight: 700;
      color: var(--text-dark);
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.95rem;
      color: var(--text-medium);
      line-height: 1.6;
    }
  }
`;

const BackButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 2rem;
  transition: var(--transition);

  &:hover {
    color: var(--secondary-red);

    svg {
      transform: translateX(-4px);
    }
  }

  svg {
    transition: var(--transition);
  }
`;

const PartnersSection = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;
`;

const PartnersTitle = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--accent-gold);
  }
`;

const PartnersList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PartnerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--background-light);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--accent-gold);
  color: var(--text-dark);
  font-size: 0.95rem;
  transition: var(--transition);

  &:hover {
    background: var(--white);
    box-shadow: var(--shadow-sm);
  }

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
  }
`;

const Cooperation = () => {
  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Coopération Internationale</HeroTitle>
          <HeroSubtitle>
            La Médiature de la République du Tchad entretient des partenariats 
            stratégiques avec des organisations nationales et internationales
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <ContentWrapper>
          <BackButton href="/partenaires">
            <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
            Retour aux partenaires
          </BackButton>

          <AOMASection>
            <div className="badge">
              <Award size={20} />
              Membre actif
            </div>
            <h2>Association des Ombudsmans et Médiateurs Africains (AOMA)</h2>
            <p>
              La Médiature de la République du Tchad est fière membre de l'AOMA, 
              une organisation qui regroupe les institutions de médiation du continent 
              africain. Cette appartenance permet d'échanger les meilleures pratiques, 
              de participer à des formations communes et de renforcer la coopération 
              Sud-Sud en matière de protection des citoyens.
            </p>
          </AOMASection>

          <PartnersSection>
            <PartnersTitle>
              <Globe size={24} />
              Missions diplomatiques et organisations internationales
            </PartnersTitle>
            <PartnersList>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>Union Européenne (UE)</span>
              </PartnerItem>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>Programme des Nations Unies pour le Développement (PNUD)</span>
              </PartnerItem>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>UNICEF</span>
              </PartnerItem>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>Haut-Commissariat aux Droits de l'Homme (HCDH)</span>
              </PartnerItem>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>Toutes les ambassades accréditées au Tchad</span>
              </PartnerItem>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>Coopération Allemande (GIZ)</span>
              </PartnerItem>
              <PartnerItem>
                <ChevronRight size={20} />
                <span>Banque Mondiale</span>
              </PartnerItem>
            </PartnersList>
          </PartnersSection>

          <ContentGrid>
            <ContentCard>
              <CardHeader>
                <div className="icon">
                  <Handshake size={28} />
                </div>
                <h3>Collaborations bilatérales</h3>
              </CardHeader>
              <CardText>
                La Médiature entretient des relations étroites avec des Médiateurs 
                et Ombudsmans d'autres pays. Ces échanges permettent de partager 
                des expériences, d'apprendre des approches innovantes et de développer 
                des stratégies communes pour relever les défis de la médiation 
                administrative à l'ère du numérique.
              </CardText>
            </ContentCard>

            <ContentCard>
              <CardHeader>
                <div className="icon">
                  <Globe size={28} />
                </div>
                <h3>Partenariats techniques</h3>
              </CardHeader>
              <CardText>
                La Médiature noue des partenariats avec des organisations nationales 
                et internationales pour la réalisation de projets de sensibilisation 
                à la paix, au bon vivre-ensemble, au renforcement des capacités et 
                aux formations. Ces collaborations permettent d'accéder à des ressources 
                et expertises complémentaires.
              </CardText>
            </ContentCard>
          </ContentGrid>

          <BenefitsSection>
            <BenefitsTitle>Les bénéfices de la coopération internationale</BenefitsTitle>
            <BenefitsGrid>
              <BenefitCard>
                <div className="icon">
                  <Lightbulb size={28} />
                </div>
                <h4>Expertise renforcée</h4>
                <p>Accès à des formations, ateliers et séminaires internationaux pour les agents de la Médiature</p>
              </BenefitCard>
              <BenefitCard>
                <div className="icon">
                  <Award size={28} />
                </div>
                <h4>Crédibilité accrue</h4>
                <p>Reconnaissance internationale et alignement sur les standards de l'Institution du Médiateur</p>
              </BenefitCard>
              <BenefitCard>
                <div className="icon">
                  <Target size={28} />
                </div>
                <h4>Efficacité améliorée</h4>
                <p>Adoption des meilleures pratiques et outils modernes de médiation et de résolution des conflits</p>
              </BenefitCard>
            </BenefitsGrid>
          </BenefitsSection>

          <ProjectsSection>
            <ProjectsTitle>
              <Users size={24} />
              Domaines de coopération
            </ProjectsTitle>
            <ProjectList>
              <ProjectItem>
                <ChevronRight size={20} />
                <div className="content">
                  <h4>Sensibilisation à la paix et au vivre-ensemble</h4>
                  <p>Programmes de médiation communautaire et de prévention des conflits dans les régions du Tchad</p>
                </div>
              </ProjectItem>
              <ProjectItem>
                <ChevronRight size={20} />
                <div className="content">
                  <h4>Renforcement des capacités</h4>
                  <p>Formation des médiateurs locaux et des acteurs de la société civile aux techniques de médiation</p>
                </div>
              </ProjectItem>
              <ProjectItem>
                <ChevronRight size={20} />
                <div className="content">
                  <h4>Appui institutionnel</h4>
                  <p>Modernisation des outils de travail, digitalisation des procédures et amélioration de l'accès au service</p>
                </div>
              </ProjectItem>
              <ProjectItem>
                <ChevronRight size={20} />
                <div className="content">
                  <h4>Recherche et documentation</h4>
                  <p>Études comparatives, publication de rapports et contribution aux réflexions sur la bonne gouvernance</p>
                </div>
              </ProjectItem>
            </ProjectList>
          </ProjectsSection>
        </ContentWrapper>
      </Container>
    </PageContainer>
  );
};

export default Cooperation;
